import React from 'react';
import Helmet from 'react-helmet';
import Link from 'gatsby-link';
import _ from 'lodash';

import config from '../../config';

// Components
import Card from '../../components/Card';
import Logo from '../../components/Logo';
import ResearchCard from '../../components/ResearchCard';
import Header from '../../components/Header';
import NutrientStats from '../../components/NutrientStats';
import Leaderboard from '../../components/Leaderboard';
import UnfinishedBanner from '../../components/UnfinishedBanner';
import {
  FlaskIcon,
  MoleculeIcon,
  MunchIcon,
  PodiumIcon,
  HeartIcon
} from '../../components/AnimatedIcons';

// Icons
import { Link as LinkIcon } from 'material-ui-icons';

// Utils
import {
  createSectionId,
  formatVitaminName,
  getAltVitaminName,
  getContinuousVerb,
  getDeterminer,
  getShortNutrientName,
  iuToMcg,
  formatServingText
} from '../../utils';

// Styles
import {
  StyledWrapper,
  LeaderboardsWrapper,
  TopSection,
  TitleSection,
  CenteredHeading,
  NutrientsSection,
  NutrientsHeader,
  ServingText,
  EnterFromLeftAnimation,
  HomeLinkWrap,
  CardsSection,
  ResearchSection,
  spinLogo,
  AbstractLogosSectionTop,
  AbstractLogosSectionBottom,
  TooMuchSection,
  MissingNutrientText
} from './FoodTemplateStyles';

export default function Template(props) {
  const content = props.pathContext.content;
  const allNutrients = props.pathContext.allNutrients;
  const longTitle = `${/[^u](s|fruit)+$/i.test(content.title) ? 'Are' : 'Is'} ${content.title} good for you?`;
  const baseColor = content.themecolor || 'rgb(0, 0, 0)';
  const themeColors = baseColor.split('-');
  const textColor = content.textcolor || 'rgb(255, 255, 255)';
  const serving = content.serving || 100;
  const servingText = formatServingText(serving, content.servingtext, content.title);
  const description = !/^Lorem/.test(content.description) && content.description;
  const sourcing = !/^Lorem/.test(content.sourcing) && content.sourcing;
  const featuredData = content.featuredData && content.featuredData.length ? content.featuredData : null;

  const meta = {
    title: `Health benefits of ${getContinuousVerb(content.verb)} ${content.title}`,
    description: `${longTitle} ${description ? description.substring(0, 180) : ''}`
  };

  const isWIP = !content.researchData || !content.researchData.length;

  return (
    <StyledWrapper themeColors={themeColors} textColor={textColor}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name='description' content={meta.description} />
      </Helmet>
      <Header themeColors={themeColors} backLink={true} />
      <TopSection id={`${longTitle.toLowerCase().replace(/\s/g, '-').replace('?', '')}`}>
        <TitleSection>
          <HeartIcon
            width={80}
            height={80}
            color={textColor} />
          <h1><Link to={`#${longTitle.toLowerCase().replace(/\s/g, '-').replace('?', '')}`}><LinkIcon className='icon icon--link-prompt' style={{ width: 24, height: 24 }} />{longTitle}</Link></h1>
          <p>{description}</p>
          <section>
            <CardsSection textColor={textColor}>
              {content.tags.map(t => <Card id={t.tag} key={t.tag} {...t} />)}
            </CardsSection>
          </section>
          <HomeLinkWrap themeColors={themeColors} textColor={textColor}>
            <Link to='/'>&larr; Home</Link>
          </HomeLinkWrap>
        </TitleSection>
        <NutrientsSection id='nutrients'>
          <NutrientsHeader>
            <MoleculeIcon
              ringColor={textColor}
              centerColor={textColor}
              electronColor={textColor}
              width={80}
              height={80} />
            <h1><Link to='#nutrients'><LinkIcon className='icon icon--link-prompt' style={{ width: 24, height: 24 }} />Nutrients</Link></h1>
            <ServingText serving={serving}>
              Nutritional value in {servingText}...<br /><i>(% RDA in {serving}g)</i>
            </ServingText>
            {serving < 10 && <p><i>Bear in mind, the bars below will be very small, because the serving is tiny.</i></p>}
          </NutrientsHeader>
          {content.nutrients && content.nutrients.length
            ? (
              <NutrientStats
                keys={[
                  { label: 'Females', key: 'f' },
                  { label: 'Males', key: 'm' }
                ]} data={content.nutrients}
                allNutrients={allNutrients}
                serving={parseFloat(content.serving) || 100} />
            )
            : <MissingNutrientText textColor={textColor}>Sorry, the nutrient data for {content.title} is currently missing. Please check back in the next couple of weeks.</MissingNutrientText>
          }
      </NutrientsSection>
      </TopSection>
      {isWIP && <UnfinishedBanner />}
      <ResearchSection id={createSectionId(content.title, 'research')}>
        <h1><Link to={`#${createSectionId(content.title, 'research')}`}><LinkIcon className='icon icon--link-prompt' style={{ width: 24, height: 24 }} />Medical Research into {getContinuousVerb(content.verb)} {content.title}</Link></h1>
        <div>
          {content.researchData && content.researchData.length
            ? content.researchData.map(node => (
              <ResearchCard
                key={node.id}
                {...node}
                content={content} />
            ))
          : <p>There is no research data yet, but this will be updated very soon! <a href={`https://www.google.co.uk/search?q=${content.title.replace(' ', '+').toLowerCase()}+medical+research`} target='_blank'>Check Google &rarr;</a></p>}
        </div>
      </ResearchSection>
      {featuredData && (
      <LeaderboardsWrapper textColor={textColor}>
        <PodiumIcon
          width={120}
          height={120}
          colors={[textColor]}
          nodeColors={[themeColors[0], themeColors[0], themeColors[0]]}
          strokeColor={textColor} />
        <h1>Lists Containing {content.title}</h1>
        <div>
        {featuredData.map(({ id, title, description, image, items }) => (
          <Leaderboard
            key={id}
            image={image}
            title={title}
            description={description}
            items={items}
            split={true} />
        ))}
        </div>
      </LeaderboardsWrapper>)}
      <TooMuchSection id={createSectionId(content.title, 'too-much')}>
        <CenteredHeading>
          <MunchIcon
            width={120}
            height={120}
            strokeColor={'black'}
            foodColor={themeColors[0]} />
          <h1><Link to={`#${createSectionId(content.title, 'too-much')}`}><LinkIcon className='icon icon--link-prompt' style={{ width: 24, height: 24 }} />Can I {content.verb || 'eat'} too {getDeterminer(content.title)} {content.title}?</Link></h1>
        </CenteredHeading>
        <p>{/^Lorem/.test(content.toomuchcauses) ? config.missingText.toomuchcauses : content.toomuchcauses}</p>
      </TooMuchSection>
    </StyledWrapper>
  );
};



// WEBPACK FOOTER //
// ./src/templates/FoodTemplate/FoodTemplate.js